import { useEffect, useRef, useState } from 'react';
import {
  CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TABLE_MAX_HEIGHT } from '../../theme';
import { formatDate, fromNow } from '../../utils/dates';
import { NAME_WIDTH } from '../ads/cells';
import StatusBadge from '../status/StatusBadge';
import StickyTableContainer from '../table/StickyTableContainer';
import AccountDetailsDialog, { AccountIdentity, accountTitle } from './AccountDetailsDialog';
import { connectionHealth } from './health';

// connection health -> StatusBadge
const STATUS = {
  ok: { label: 'Active', tone: 'success' },
  warning: { label: 'Expiring soon', tone: 'warning' },
  error: { label: 'Needs reconnect', tone: 'error' },
};

const EXPIRY_COLOR = { ok: 'text.primary', warning: 'warning.dark', error: 'error.main' };

/**
 * One row per connected account of a site. pendingId is the account being reconnected; highlightId the account a
 * login just added or updated. showApp adds the App column; canReconnect(connection) is false for an account whose
 * app cannot log in (inactive).
 */
export default function ConnectionsTable({
  connections, meta, pendingId, highlightId, disabled, onReconnect, showApp, canReconnect = () => true,
}) {
  const [detailsId, setDetailsId] = useState(null);
  const details = connections.find((connection) => connection.id === detailsId);
  const columns = showApp ? 8 : 7;

  return (
    <>
      <StickyTableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="medium" sx={{ minWidth: 820 }} aria-label={`${meta.name} connections`}>
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              {showApp && <TableCell>App</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Token expires</TableCell>
              <TableCell align="right">Ad accounts</TableCell>
              <TableCell>Connected via</TableCell>
              <TableCell>Last connected</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ fontWeight: 500 }}>No {meta.name} accounts connected yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use Login with {meta.name} above to add one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : connections.map((connection) => (
              <ConnectionRow key={connection.id} connection={connection} meta={meta} showApp={showApp}
                             disabled={disabled} inactiveApp={!canReconnect(connection)}
                             busy={pendingId === connection.id} highlight={highlightId === connection.id}
                             onDetails={() => setDetailsId(connection.id)}
                             onReconnect={() => onReconnect(connection)} />
            ))}
          </TableBody>
        </Table>
      </StickyTableContainer>

      <AccountDetailsDialog connection={details} meta={meta} open={Boolean(details)}
                            disabled={disabled || (details && !canReconnect(details))}
                            busy={pendingId === detailsId} onClose={() => setDetailsId(null)}
                            onReconnect={() => onReconnect(details)} />
    </>
  );
}

function ConnectionRow({
  connection, meta, showApp, busy, disabled, inactiveApp, highlight, onDetails, onReconnect,
}) {
  const ref = useRef(null);
  const health = connectionHealth(connection);
  const status = STATUS[health.level];
  const title = accountTitle(connection);
  const lastConnected = connection.sso_updated_on || connection.issued_at;

  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlight]);

  return (
    <TableRow ref={ref} hover className={highlight ? 'highlighted' : undefined}
              sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell>
        <AccountIdentity connection={connection} color={meta.color} width={NAME_WIDTH} />
      </TableCell>
      {showApp && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {connection.app_name || <Typography variant="body2" color="text.secondary">—</Typography>}
        </TableCell>
      )}
      <TableCell>
        <StatusBadge tone={status.tone} label={status.label} title={health.prompt} />
      </TableCell>
      <TableCell>
        {connection.expires_at ? (
          <>
            <Typography variant="body2" color={EXPIRY_COLOR[health.level]}>{fromNow(connection.expires_at)}</Typography>
            <Typography variant="caption" color="text.secondary">{formatDate(connection.expires_at)}</Typography>
          </>
        ) : <Typography variant="body2" color="text.secondary">Never</Typography>}
      </TableCell>
      <TableCell align="right">{connection.ad_account_count ?? '—'}</TableCell>
      <TableCell>{connection.is_manual ? 'Pasted token' : `${meta.name} login`}</TableCell>
      <TableCell>
        {lastConnected ? (
          <Typography variant="body2" title={formatDate(lastConnected)}>{fromNow(lastConnected)}</Typography>
        ) : '—'}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Details">
            <IconButton size="small" onClick={onDetails} aria-label={`Details of ${title}`}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={inactiveApp ? 'Its app is inactive, so it cannot reconnect' : 'Reconnect'}>
            <span>
              <IconButton size="small" onClick={onReconnect} disabled={disabled || inactiveApp}
                          aria-label={`Reconnect ${title}`}>
                {busy ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
